/* ============================================================
   PHREE SKIN — the Braille dot field.
   The grid is DORMANT (dots near-invisible). Random BRAILLE-CELL
   configurations bloom into existence at grid positions, hold, then
   dissolve — like letters surfacing and fading across the video.
   Dots never drift: they sit exactly on a grid whose pitch is a
   multiple of the logo's Braille grid, all one fixed size.
   <canvas data-dots data-color="115,179,206" data-gap="30" data-dot="3" data-alpha="0.85"
           data-mouse-target="#hero">

   ROBUSTNESS (2026-07 fix): the field must appear even when the page is
   opened in a BACKGROUND tab (the common "View your store" flow, which
   opens a new tab you're not looking at yet). While a tab is hidden the
   browser pauses IntersectionObserver AND requestAnimationFrame, so the
   old "start only when the IO first fires" logic could leave the canvas
   blank. Now: we start directly if the canvas is in view, paint one frame
   synchronously (the lattice shows the instant you glance over), and
   re-kick on `visibilitychange`. Under reduced-motion we draw a static
   field instead of nothing.
   ============================================================ */
(function () {
  "use strict";
  var reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var DPR = Math.min(2, window.devicePixelRatio || 1);

  /* Braille-cell patterns — [col,row] within a 2-wide × 3-tall cell (some 3-tall+ for variety). */
  var GLYPHS = [
    [[0,0],[0,1],[0,2]],
    [[0,0],[1,0]],
    [[0,0],[0,1],[0,2],[1,0]],          // ~ the brand "Ph" feel
    [[1,0],[0,1],[0,2]],
    [[0,0],[1,1],[0,2]],
    [[0,0],[0,1],[1,1],[1,2]],
    [[0,0],[1,0],[0,1],[1,2]],
    [[0,1],[1,0],[1,1]],
    [[0,0],[0,2],[1,1]],
    [[1,0],[1,1],[1,2],[0,0]]
  ];

  function debounce(fn, ms) { var t; return function () { clearTimeout(t); t = setTimeout(fn, ms); }; }

  document.querySelectorAll("canvas[data-dots]").forEach(function (canvas) {
    var ctx = canvas.getContext("2d");
    var color = canvas.dataset.color || "115,179,206";
    var gap = +canvas.dataset.gap || 30;
    var dotR = canvas.dataset.dot ? +canvas.dataset.dot : 3;
    var peak = canvas.dataset.alpha ? +canvas.dataset.alpha : 0.85;   // glyph peak brightness
    var substrate = 0.05;                                              // dormant grid whisper
    var target = canvas.dataset.mouseTarget ? document.querySelector(canvas.dataset.mouseTarget) : canvas.parentElement;
    var cw = 0, ch = 0, cols = 0, rows = 0, running = false, raf = 0, last = 0;
    var glyphs = [], mouse = { x: -9999, y: -9999 };

    function resize() {
      var r = canvas.getBoundingClientRect(); cw = r.width; ch = r.height;
      canvas.width = Math.max(1, cw * DPR); canvas.height = Math.max(1, ch * DPR);
      cols = Math.floor(cw / gap); rows = Math.floor(ch / gap);
    }
    function spawn(t) {
      if (cols < 4 || rows < 5) return;
      var g = GLYPHS[(Math.random() * GLYPHS.length) | 0];
      glyphs.push({
        g: g,
        gx: 1 + ((Math.random() * (cols - 3)) | 0),
        gy: 1 + ((Math.random() * (rows - 4)) | 0),
        t0: t, ttl: 2000 + Math.random() * 2200
      });
    }
    function dot(x, y, a) {
      if (a <= 0.012) return;
      ctx.fillStyle = "rgba(" + color + "," + (a > 1 ? 1 : a).toFixed(3) + ")";
      ctx.beginPath(); ctx.arc(x, y, dotR, 0, 6.2832); ctx.fill();
    }
    /* dormant grid whisper — almost nothing, just a hint of the lattice (+ cursor draw-out) */
    function drawGrid() {
      for (var iy = 0; iy <= rows; iy++) for (var ix = 0; ix <= cols; ix++) {
        var x = ix * gap, y = iy * gap, a = substrate;
        var dx = mouse.x - x, dy = mouse.y - y, md = Math.sqrt(dx * dx + dy * dy);
        if (md < 120) a += 0.5 * (1 - md / 120);
        dot(x, y, a);
      }
    }
    function step(t) {
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      ctx.clearRect(0, 0, cw, ch);
      drawGrid();
      /* spawn + render Braille glyphs */
      if (t - last > 620 && glyphs.length < 7) { spawn(t); last = t; }
      for (var i = glyphs.length - 1; i >= 0; i--) {
        var gl = glyphs[i], p = (t - gl.t0) / gl.ttl;
        if (p >= 1) { glyphs.splice(i, 1); continue; }
        var a = Math.sin(p * Math.PI) * peak;              // fade in → hold → fade out
        for (var k = 0; k < gl.g.length; k++) dot((gl.gx + gl.g[k][0]) * gap, (gl.gy + gl.g[k][1]) * gap, a);
      }
      if (running) raf = requestAnimationFrame(step);
    }
    function paintOnce() {                                  // synchronous single frame — visible even in a throttled tab
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0); ctx.clearRect(0, 0, cw, ch); drawGrid();
    }
    function start() {
      /* Always repaint, and always re-arm — never early-return on `running`.
         The wedge this fixes: the script runs deferred, so if the first start()
         happens while the tab is throttled (or before the hero has laid out),
         paintOnce() paints nothing AND `running` latches true. Every later
         kick() — including the one on visibilitychange when the tab finally
         becomes visible — then hit `if (running) return` and did nothing, so
         the field stayed blank forever. cancelAnimationFrame on a stale id is
         a no-op, so re-arming here cannot produce two concurrent loops. */
      resize();                                            // re-measure: the first resize may have run pre-layout
      paintOnce();                                         // show the lattice instantly, don't wait for the first rAF
      cancelAnimationFrame(raf);
      running = true;
      raf = requestAnimationFrame(step);
    }
    function stop() { running = false; cancelAnimationFrame(raf); }
    function inView() { var r = canvas.getBoundingClientRect(); return r.top < (window.innerHeight || 0) && r.bottom > -40; }
    function kick() { if (inView()) start(); }

    /* reduced-motion: no animation, but never a blank hero — one static frame
       (dormant grid + a few fixed glyphs so the Braille texture reads). */
    function staticFrame() {
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0); ctx.clearRect(0, 0, cw, ch); drawGrid();
      if (cols >= 4 && rows >= 5) {
        for (var n = 0; n < 4; n++) {
          var g = GLYPHS[(n * 2) % GLYPHS.length];
          var gx = 1 + ((n * 7 + 3) % (cols - 3)), gy = 1 + ((n * 5 + 2) % (rows - 4));
          for (var k = 0; k < g.length; k++) dot((gx + g[k][0]) * gap, (gy + g[k][1]) * gap, peak * 0.55);
        }
      }
    }

    (target || canvas).addEventListener("mousemove", function (e) {
      var r = canvas.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top;
    });
    (target || canvas).addEventListener("mouseleave", function () { mouse.x = mouse.y = -9999; });

    resize();

    if (reduce) {
      staticFrame();
      window.addEventListener("resize", debounce(function () { resize(); staticFrame(); }, 200));
      window.addEventListener("load", function () { resize(); staticFrame(); });
      return;
    }

    window.addEventListener("resize", debounce(resize, 200));
    /* IO pauses the loop when scrolled fully away (perf) and resumes it on the way back. */
    new IntersectionObserver(function (es) { es.forEach(function (en) { en.isIntersecting ? start() : stop(); }); }, { threshold: 0 }).observe(canvas);
    kick();                                                  // START NOW — the IO won't fire while the tab is backgrounded
    document.addEventListener("visibilitychange", function () { if (document.visibilityState === "visible") { resize(); kick(); } });
    window.addEventListener("load", function () { resize(); kick(); });
  });
})();
